/**
 * Framework-agnostic core of the inspection draft autosave (restored in
 * v1.5.22). Kept out of the React component so the trigger mechanics — debounce,
 * eligibility gating, and immediate flush — are unit-testable without a DOM.
 *
 * Context: the original autosave (Bolt's InspectionPage.tsx) was lost in the M7
 * port and the regression slipped because the lifecycle e2e test only exercised
 * the PUT endpoint, never the client trigger. This module is the missing
 * trigger logic, now covered by inspectionAutosave.test.ts.
 */

/** Fires this many ms after the last inspection edit settles (debounced). */
export const AUTOSAVE_DEBOUNCE_MS = 4000;

export interface AutosaveEligibility {
  /** A genuine user edit happened (not just loadExistingReport populating state). */
  userEdited: boolean;
  /** A save is already in flight. */
  isSaving: boolean;
  /** There is something worth saving. */
  hasContent: boolean;
  /** A draft/report record already exists. */
  hasReport: boolean;
}

/** Pure decision: may an autosave run right now? */
export function shouldAutosave(e: AutosaveEligibility): boolean {
  if (!e.userEdited || e.isSaving) return false;
  // Don't create an empty draft; once a draft exists, keep it saved.
  if (!e.hasContent && !e.hasReport) return false;
  return true;
}

/** True when any inspection list has entries. */
export function hasInspectionContent(
  ...lists: ReadonlyArray<{ length: number }>
): boolean {
  return lists.some((l) => l.length > 0);
}

export interface AutosaveScheduler {
  /** Call after each inspection edit; (re)starts the debounce. */
  onChange(): void;
  /** Save now if eligible (e.g. on tab-hide / page-unload). Clears any pending timer. */
  flush(): void;
  /** Cancel a pending debounced save (e.g. a manual Save/Submit pre-empts it). */
  cancel(): void;
}

/**
 * Debounced autosave scheduler. Uses the global timer functions so vitest fake
 * timers can drive it; eligibility + save are read through callbacks so the
 * caller can supply the latest state at fire time.
 */
export function createAutosaveScheduler(
  getEligibility: () => AutosaveEligibility,
  save: () => void,
  debounceMs: number = AUTOSAVE_DEBOUNCE_MS,
): AutosaveScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  const run = () => {
    if (shouldAutosave(getEligibility())) save();
  };
  return {
    onChange() {
      // Don't even arm the timer until the user has actually edited.
      if (!getEligibility().userEdited) return;
      clear();
      timer = setTimeout(() => {
        timer = null;
        run();
      }, debounceMs);
    },
    flush() {
      clear();
      run();
    },
    cancel: clear,
  };
}
